import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { SESSION_MODES, newIdempotencyKey, programSummary, trainingError } from "../lib/training";
import { ProspectFilter } from "../components/TaskFormSheet";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Loader2, Search } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

// « Nouvelle session ». From a mission (?mission=): on site or visio, at that mission's pharmacy, trainers from its team — for whoever
// organises the mission. Without mission: visio or internal, any trainer — training:manage_all only (decision 3). The server decides.
export default function TrainingSessionFormPage() {
  const [params] = useSearchParams();
  const missionId = params.get("mission");
  const navigate = useNavigate();
  const [phase, setPhase] = useState("loading"); // loading | ready | error
  const [error, setError] = useState("");
  const [mission, setMission] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [people, setPeople] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ program_id: "", mode: missionId ? "on_site" : "visio", date: today(), start_time: "", trainers: [], notes: "" });
  const [pharmacy, setPharmacy] = useState(null);
  const [warnings, setWarnings] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState(newIdempotencyKey);

  useEffect(() => {
    (async () => {
      try {
        const [list, missionRes] = await Promise.all([api.get("/training/programs"),
          missionId ? api.get(`/missions/${missionId}`) : Promise.resolve(null)]);
        setPrograms(list.data.items.filter((p) => p.active && p.current_version));
        if (missionRes) {
          setMission(missionRes.data);
          if (missionRes.data.planned_start_at) setForm((f) => ({ ...f, date: missionRes.data.planned_start_at.slice(0, 10) }));
        }
        setPhase("ready");
      } catch (e) {
        setError(trainingError(e, "Impossible de préparer la session."));
        setPhase("error");
      }
    })();
  }, [missionId]);

  // Trainers: the mission's team, or (without mission) accounts and fiches matching the search.
  useEffect(() => {
    if (phase !== "ready") return undefined;
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/training/sessions/trainer-candidates", { params: missionId ? { mission_id: missionId } : { search } });
        setPeople(data.people);
      } catch (e) {
        setPeople([]);
      }
    }, missionId ? 0 : 250);
    return () => clearTimeout(handle);
  }, [phase, missionId, search]);

  // Any change of content is a new submission: a fresh key, and warnings to look at again.
  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setWarnings(null); setConfirmed(false); setKey(newIdempotencyKey()); };
  const toggleTrainer = (person) => {
    const exists = form.trainers.some((t) => t.kind === person.kind && t.id === person.id);
    set({ trainers: exists ? form.trainers.filter((t) => !(t.kind === person.kind && t.id === person.id)) : [...form.trainers, person] });
  };
  const program = programs.find((p) => p.id === form.program_id);
  const modes = missionId ? ["on_site", "visio"] : ["visio", "internal"];
  const problem = useMemo(() => {
    if (!form.program_id) return "Choisissez un programme publié.";
    if (!form.date) return "Choisissez la date.";
    if (!form.trainers.length) return "Choisissez au moins un formateur.";
    return null;
  }, [form]);

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        program_id: form.program_id, mode: form.mode, date: form.date, start_time: form.start_time || null, notes: form.notes.trim() || null,
        trainers: form.trainers.map((t) => ({ kind: t.kind, id: t.id })),
        ...(missionId ? { mission_id: missionId } : form.mode === "visio" && pharmacy ? { prospect_id: pharmacy.id } : {}),
        confirm_warnings: confirmed && warnings ? [...new Set(warnings.map((w) => w.kind))] : [],
      };
      const { data } = await api.post("/training/sessions", body, { headers: { "Idempotency-Key": key } });
      toast.success(`Session ${data.reference} préparée`);
      navigate(`/formations/sessions/${data.id}`, { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "warnings") setWarnings(detail.warnings);
      else toast.error(trainingError(e, "Impossible de préparer la session."));
    } finally {
      setBusy(false);
    }
  };

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-8 w-72" /><Skeleton className="h-96 w-full" /></div>;
  if (phase === "error") {
    return (
      <Card className="p-8 text-center space-y-3 max-w-xl mx-auto" data-testid="session-form-error">
        <AlertTriangle className="mx-auto text-slate-300" />
        <p className="text-sm text-slate-700">{error}</p>
        <Button variant="outline" asChild><Link to="/formations">Retour aux formations</Link></Button>
      </Card>
    );
  }

  const back = mission ? `/missions/${mission.id}` : "/formations";
  return (
    <div className="space-y-4" data-testid="session-form-page">
      <Link to={back} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ArrowLeft size={14} /> {mission ? "Retour à la mission" : "Retour aux formations"}
      </Link>
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Nouvelle session de formation</h1>
        <p className="text-sm text-slate-500">
          {mission ? `Pendant la mission ${mission.reference} · ${mission.title}${mission.prospect?.pharmacy_name ? ` · ${mission.prospect.pharmacy_name}` : ""}.`
            : "Une visio ou une formation interne, sans mission."}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] items-start max-w-6xl">
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (!problem) submit(); }}>
          <Card className="p-4 sm:p-5 space-y-4">
            <h2 className="text-base font-semibold text-slate-900">Programme</h2>
            {programs.length === 0 ? (
              <p className="text-sm text-slate-500">Aucun programme publié. <Link to="/formations?tab=programmes" className="text-jamora underline">Voir les programmes</Link></p>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="session-program">Programme *</Label>
                <Select value={form.program_id} onValueChange={(value) => set({ program_id: value })}>
                  <SelectTrigger id="session-program" data-testid="session-program"><SelectValue placeholder="Choisir un programme" /></SelectTrigger>
                  <SelectContent>
                    {programs.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`session-program-${p.code}`}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {program && <p className="text-xs text-slate-500">Version {program.current_version.number} · {programSummary(program.current_version)}</p>}
              </div>
            )}
          </Card>

          <Card className="p-4 sm:p-5 space-y-4">
            <h2 className="text-base font-semibold text-slate-900">Quand et comment</h2>
            <RadioGroup value={form.mode} onValueChange={(value) => set({ mode: value })} className="flex flex-wrap gap-4">
              {modes.map((m) => (
                <label key={m} htmlFor={`session-mode-${m}`} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <RadioGroupItem id={`session-mode-${m}`} value={m} data-testid={`session-mode-${m}`} />{SESSION_MODES[m]}
                </label>
              ))}
            </RadioGroup>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Label htmlFor="session-date">Date *</Label>
                <Input id="session-date" type="date" className="text-base md:text-sm" value={form.date} onChange={(e) => set({ date: e.target.value })}
                  data-testid="session-date" /></div>
              <div className="space-y-1.5"><Label htmlFor="session-time">Heure</Label>
                <Input id="session-time" type="time" className="text-base md:text-sm" value={form.start_time} onChange={(e) => set({ start_time: e.target.value })} /></div>
            </div>
            {!missionId && form.mode === "visio" && (
              <div className="space-y-1.5">
                <Label>Pharmacie</Label>
                <ProspectFilter value={pharmacy} onChange={(p) => { setPharmacy(p); setWarnings(null); setKey(newIdempotencyKey()); }} />
                <p className="text-xs text-slate-500">Facultatif : sans pharmacie, seuls des collaborateurs Jamora pourront y participer.</p>
              </div>
            )}
          </Card>

          <Card className="p-4 sm:p-5 space-y-3">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Formateurs *</h2>
              <p className="text-xs text-slate-500">{missionId ? "Parmi l'équipe de la mission." : "Un compte du CRM ou une fiche de l'annuaire."}</p>
            </div>
            {!missionId && (
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une personne" className="pl-8 text-base md:text-sm"
                  aria-label="Rechercher un formateur" />
              </div>
            )}
            <div className="space-y-1 max-h-72 overflow-y-auto scrollbar-thin" data-testid="session-trainers">
              {[...form.trainers.filter((t) => !people.some((p) => p.kind === t.kind && p.id === t.id)), ...people].map((p) => (
                <label key={`${p.kind}-${p.id}`} className="flex items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-slate-50 cursor-pointer"
                  data-testid={`session-trainer-${p.id}`}>
                  <Checkbox checked={form.trainers.some((t) => t.kind === p.kind && t.id === p.id)} onCheckedChange={() => toggleTrainer(p)} />
                  <span className="text-sm text-slate-800">{p.name}</span>
                  {p.kind === "collaborator" && <span className="text-xs text-slate-500">sans compte CRM</span>}
                </label>
              ))}
              {people.length === 0 && <p className="text-sm text-slate-500 px-2">{missionId ? "L'équipe de la mission est vide." : "Aucune personne trouvée."}</p>}
            </div>
          </Card>

          <Card className="p-4 sm:p-5 space-y-1.5">
            <Label htmlFor="session-notes">Notes de préparation</Label>
            <Textarea id="session-notes" rows={3} maxLength={4000} className="text-base md:text-sm" value={form.notes} onChange={(e) => set({ notes: e.target.value })}
              placeholder="Ex. salle, matériel à apporter, personnes attendues" />
          </Card>

          {warnings && (
            <Card className="p-4 border-amber-300 bg-amber-50 space-y-2" data-testid="session-warnings" role="alert">
              <p className="text-sm font-medium text-amber-900">À vérifier avant de confirmer</p>
              <ul className="space-y-1 text-sm text-amber-900">
                {warnings.map((w, i) => (
                  <li key={i}>{w.trainer.name} n'a pas d'habilitation valide ce jour-là pour : {w.skills.join(", ")}.</li>
                ))}
              </ul>
              <label className="flex items-center gap-2 text-sm text-amber-900 cursor-pointer">
                <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} data-testid="session-warnings-confirm" />
                Je confirme : ce formateur anime quand même la session.
              </label>
            </Card>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
            {problem && <p className="text-sm text-slate-500 sm:mr-auto">{problem}</p>}
            <Button type="button" variant="outline" asChild><Link to={back}>Annuler</Link></Button>
            <Button type="submit" disabled={busy || !!problem || (warnings && !confirmed)} data-testid="session-submit">
              {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Préparer la session
            </Button>
          </div>
        </form>
        <aside className="space-y-4">
          <Card className="p-4 space-y-2 text-sm text-slate-600">
            <h2 className="text-sm font-semibold text-slate-900">Comment ça se passe</h2>
            <p>La session garde la version publiée du programme : la modifier ensuite ne change pas ce qui sera évalué.</p>
            <p>Le jour J, le formateur ajoute les participants, indique leur présence, coche les modules traités, note chaque objectif puis clôture.</p>
            <p>Pour le personnel d'une pharmacie, seuls le prénom, le nom et le rôle Jamora sont enregistrés, jamais de coordonnées.</p>
          </Card>
        </aside>
      </div>
    </div>
  );
}
